import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import {
    get_my_installments,
    get_my_installment,
    confirm_flw_installment,
    messageClear,
    clearSelectedInstallment
} from '../../store/reducers/installmentReducer'
import { formatCurrency } from '../../utils/currencyUtils'
import { useCurrency } from '../../hooks/useCurrency'
import { CalendarClock, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle, CreditCard, Building2, Loader2, Lock, ShieldCheck, X } from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_BADGE = {
    active: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500'
}

const SCHEDULE_BADGE = {
    pending: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700'
}

const ScheduleIcon = ({ status }) => {
    if (status === 'paid') return <CheckCircle2 size={16} className='text-green-500' />
    if (status === 'overdue') return <AlertCircle size={16} className='text-red-500' />
    return <Clock size={16} className='text-yellow-500' />
}

// Card payment form for installments
const PayNowButton = ({ installmentId, scheduleItem, scheduleIdx }) => {
    const [showForm, setShowForm] = useState(false)
    const [step, setStep] = useState('card')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [chargeId, setChargeId] = useState(null)
    const [cardNumber, setCardNumber] = useState('')
    const [expiry, setExpiry] = useState('')
    const [cvv, setCvv] = useState('')
    const [pin, setPin] = useState('')
    const [otp, setOtp] = useState('')

    const formatCardNumber = (val) => {
        const v = val.replace(/\D/g, '').substring(0, 16)
        return v.replace(/(.{4})/g, '$1 ').trim()
    }
    const formatExpiry = (val) => {
        const v = val.replace(/\D/g, '').substring(0, 4)
        if (v.length > 2) return v.substring(0, 2) + '/' + v.substring(2)
        return v
    }

    const handleResponse = (data) => {
        if (data.status === 'succeeded') {
            toast.success('Payment successful!')
            window.location.reload()
            return
        }
        setChargeId(data.chargeId)
        const action = data.next_action?.type
        if (action === 'requires_pin') { setStep('pin'); setLoading(false) }
        else if (action === 'requires_otp') { setStep('otp'); setLoading(false) }
        else if (action === 'redirect_url') { window.location.href = data.next_action.redirect_url.url }
        else { setError('Unexpected response. Try a different card.'); setLoading(false) }
    }

    const submitCard = async (e) => {
        e.preventDefault()
        setError(''); setLoading(true)
        const parts = expiry.split('/')
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/init-installment`,
                {
                    installmentId, scheduleIdx,
                    card: { number: cardNumber.replace(/\s/g, ''), expiry_month: parts[0], expiry_year: parts[1], cvv }
                },
                { withCredentials: true }
            )
            handleResponse(data)
        } catch (err) {
            setError(err.response?.data?.error || 'Payment failed')
            setLoading(false)
        }
    }

    const submitPin = async (e) => {
        e.preventDefault()
        setError(''); setLoading(true)
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/authorize`,
                { chargeId, type: 'pin', pin },
                { withCredentials: true }
            )
            setPin(''); handleResponse(data)
        } catch (err) { setError(err.response?.data?.error || 'PIN failed'); setLoading(false) }
    }

    const submitOtp = async (e) => {
        e.preventDefault()
        setError(''); setLoading(true)
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/payment/authorize`,
                { chargeId, type: 'otp', otp },
                { withCredentials: true }
            )
            handleResponse(data)
        } catch (err) { setError(err.response?.data?.error || 'OTP failed'); setLoading(false) }
    }

    if (!showForm) {
        return (
            <button
                onClick={() => setShowForm(true)}
                className='flex items-center gap-1 text-xs px-3 py-1.5 bg-[#ffe11b] text-black font-semibold rounded-full hover:bg-yellow-300 transition-all'
            >
                <CreditCard size={12} /> Pay via Card
            </button>
        )
    }

    return (
        <div className='mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg'>
            <div className='flex justify-between items-center mb-2'>
                <span className='text-xs font-semibold text-gray-700'>Pay Installment</span>
                <button onClick={() => { setShowForm(false); setStep('card'); setError('') }} className='text-gray-400 hover:text-gray-600'>
                    <X size={14} />
                </button>
            </div>
            {error && <div className='mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs'>{error}</div>}

            {step === 'card' && (
                <form onSubmit={submitCard} className='space-y-2'>
                    <input type='text' inputMode='numeric' placeholder='Card Number' value={cardNumber}
                        onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                        className='w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffe11b] focus:outline-none' required />
                    <div className='flex gap-2'>
                        <input type='text' inputMode='numeric' placeholder='MM/YY' value={expiry}
                            onChange={e => setExpiry(formatExpiry(e.target.value))}
                            className='flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffe11b] focus:outline-none' required />
                        <input type='password' inputMode='numeric' placeholder='CVV' maxLength={4} value={cvv}
                            onChange={e => setCvv(e.target.value.replace(/\D/g, ''))}
                            className='flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#ffe11b] focus:outline-none' required />
                    </div>
                    <button type='submit'
                        disabled={loading || cardNumber.replace(/\s/g, '').length < 15 || expiry.length < 5 || cvv.length < 3}
                        className='w-full py-2 bg-black text-white text-sm font-bold rounded hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-1'>
                        {loading ? <><Loader2 size={14} className='animate-spin' /> Processing...</> : <><Lock size={12} /> Pay</>}
                    </button>
                </form>
            )}
            {step === 'pin' && (
                <form onSubmit={submitPin} className='space-y-2'>
                    <p className='text-xs text-gray-600 text-center'>Enter your card PIN</p>
                    <input type='password' inputMode='numeric' maxLength={6} placeholder='PIN' value={pin}
                        onChange={e => setPin(e.target.value.replace(/\D/g, ''))} autoFocus
                        className='w-full px-2 py-2 text-center text-lg tracking-widest border border-gray-300 rounded focus:ring-1 focus:ring-[#ffe11b] focus:outline-none' required />
                    <button type='submit' disabled={loading || pin.length < 4}
                        className='w-full py-2 bg-black text-white text-sm font-bold rounded disabled:opacity-50 flex items-center justify-center gap-1'>
                        {loading ? <><Loader2 size={14} className='animate-spin' /> Verifying...</> : 'Continue'}
                    </button>
                </form>
            )}
            {step === 'otp' && (
                <form onSubmit={submitOtp} className='space-y-2'>
                    <p className='text-xs text-gray-600 text-center'>Enter OTP sent to your phone/email</p>
                    <input type='text' inputMode='numeric' maxLength={6} placeholder='OTP' value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} autoFocus
                        className='w-full px-2 py-2 text-center text-lg tracking-widest border border-gray-300 rounded focus:ring-1 focus:ring-[#ffe11b] focus:outline-none' required />
                    <button type='submit' disabled={loading || otp.length < 4}
                        className='w-full py-2 bg-black text-white text-sm font-bold rounded disabled:opacity-50 flex items-center justify-center gap-1'>
                        {loading ? <><Loader2 size={14} className='animate-spin' /> Verifying...</> : 'Verify & Pay'}
                    </button>
                </form>
            )}
        </div>
    )
}

const MyInstallments = () => {
    const dispatch = useDispatch()
    const { currencyCode, exchangeLoaded } = useCurrency()
    const { installments, totalInstallments, selectedInstallment, successMessage, errorMessage, loader } =
        useSelector(state => state.installment)
    const [expandedId, setExpandedId] = useState(null)
    const [bankInfo, setBankInfo] = useState(null)

    useEffect(() => {
        dispatch(get_my_installments())
    }, [dispatch])

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage)
            dispatch(messageClear())
            // Refresh installments after payment
            dispatch(get_my_installments())
            if (expandedId) dispatch(get_my_installment(expandedId))
        }
        if (errorMessage) {
            toast.error(errorMessage)
            dispatch(messageClear())
        }
    }, [successMessage, errorMessage, dispatch, expandedId])

    const toggleExpand = (id) => {
        if (expandedId === id) {
            setExpandedId(null)
            dispatch(clearSelectedInstallment())
        } else {
            setExpandedId(id)
            dispatch(get_my_installment(id))
        }
    }

    const handleFlwSuccess = (scheduleId, transaction_id) => {
        dispatch(confirm_flw_installment({
            scheduleId,
            installmentId: expandedId,
            transaction_id
        }))
    }

    const installmentDetail = selectedInstallment?._id === expandedId ? selectedInstallment : null

    if (installments.length === 0) {
        return (
            <div className='flex flex-col items-center justify-center py-16 gap-3 text-gray-400'>
                <CalendarClock size={48} />
                <p className='text-lg font-medium'>No installment plans yet</p>
                <p className='text-sm'>Orders paid via installments will appear here.</p>
            </div>
        )
    }

    return (
        <div className='p-4 bg-white text-gray-900 min-h-[400px]'>
            <h2 className='text-xl font-bold mb-5 flex items-center gap-2'>
                <CalendarClock size={20} />
                My Installment Plans
            </h2>

            <div className='flex flex-col gap-3'>
                {installments.map(inst => (
                    <div key={inst._id} className='border border-gray-200 rounded-lg overflow-hidden'>
                        {/* Header row */}
                        <div
                            className='flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors'
                            onClick={() => toggleExpand(inst._id)}
                        >
                            <div className='flex flex-col gap-1'>
                                <div className='flex items-center gap-2 flex-wrap'>
                                    <span className='text-sm font-semibold text-gray-800'>
                                        Order #{inst.orderId?._id?.slice(-8).toUpperCase() || '—'}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[inst.status] || ''}`}>
                                        {inst.status}
                                    </span>
                                </div>
                                <p className='text-xs text-gray-500'>
                                    {inst.numInstallments}x monthly ·{' '}
                                    {formatCurrency(inst.installmentAmount, currencyCode, exchangeLoaded)} per installment ·{' '}
                                    Total: {formatCurrency(inst.totalWithInterest, currencyCode, exchangeLoaded)}
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <span className='text-xs text-gray-400'>
                                    {new Date(inst.createdAt).toLocaleDateString()}
                                </span>
                                {expandedId === inst._id
                                    ? <ChevronUp size={18} className='text-gray-400' />
                                    : <ChevronDown size={18} className='text-gray-400' />
                                }
                            </div>
                        </div>

                        {/* Schedule */}
                        {expandedId === inst._id && (
                            <div className='border-t border-gray-100 p-4 bg-gray-50'>
                                {!installmentDetail ? (
                                    <p className='text-xs text-gray-400'>Loading schedule…</p>
                                ) : (
                                    <div className='flex flex-col gap-2'>
                                        <p className='text-xs font-semibold text-gray-600 mb-1'>Payment Schedule</p>
                                        {installmentDetail.schedule.map((s, sIdx) => (
                                            <div
                                                key={s._id}
                                                className='flex items-center justify-between bg-white border border-gray-100 rounded p-3'
                                            >
                                                <div className='flex items-center gap-2'>
                                                    <ScheduleIcon status={s.status} />
                                                    <div>
                                                        <p className='text-sm font-medium text-gray-700'>
                                                            Installment {s.installmentNumber}
                                                        </p>
                                                        <p className='text-xs text-gray-400'>
                                                            Due: {new Date(s.dueDate).toLocaleDateString()}
                                                            {s.paidAt && ` · Paid: ${new Date(s.paidAt).toLocaleDateString()}`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className='flex items-center gap-3'>
                                                    <span className='text-sm font-semibold'>
                                                        {formatCurrency(s.amount, currencyCode, exchangeLoaded)}
                                                    </span>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${SCHEDULE_BADGE[s.status] || ''}`}>
                                                        {s.status}
                                                    </span>
                                                    {s.status !== 'paid' && inst.status === 'active' && (
                                                        <PayNowButton
                                                            installmentId={inst._id}
                                                            scheduleItem={s}
                                                            scheduleIdx={sIdx}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Bank transfer info */}
                                        <div className='mt-2 text-xs text-gray-500 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded p-3'>
                                            <Building2 size={14} className='mt-0.5 text-blue-400 flex-shrink-0' />
                                            <span>
                                                Prefer bank transfer? Transfer the installment amount to our account and contact support with proof of payment. Admin will confirm and mark your installment as paid.
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default MyInstallments
